import { useQuery } from '@tanstack/react-query';
import { WorkflowsService } from '../../../js/api';
import { StatusCodes } from '../constants/workflows';

/**
 * Workflow Metadata Types
 *
 * These types define the structure of workflow status metadata returned from the backend.
 * Workflows define valid statuses, allowed transitions, and required fields for status changes.
 */

export interface WorkflowReason {
  code: string;
  title: string;
  require_note: boolean;
  set_by_provider: boolean;
  set_by_healthcare_entity: boolean;
  set_by_system: boolean;
  set_by_staff: boolean;
}

export interface WorkflowStatus {
  code: string;
  title: string;
  verb: string;
  category: string;
  category_title: string;
  category_kind: string;
  reasons: WorkflowReason[];
  require_reason: boolean;
  require_note: boolean;
  is_default: boolean;
  is_draft: boolean;
  is_initial: boolean;
  is_transitory: boolean;
  is_final: boolean;
  allowed_initial_status: boolean;
  set_by_provider: boolean;
  set_by_healthcare_entity: boolean;
  set_by_staff: boolean;
  set_by_system: boolean;
  provider_queue_name: string;
  healthcare_entity_queue_name: string;
  staff_queue_name: string;
  allowed_transitions: string[];
}

export type WorkflowMetadata = {
  [status in StatusCodes]?: WorkflowStatus;
} & {
  [status: string]: WorkflowStatus | undefined;
};

export interface WorkflowMetadataResponse {
  [scope: string]: WorkflowMetadata;
}

export type StatusMetadata = Record<string, WorkflowStatus>;

/**
 * Workflow Metadata Manager
 *
 * This hook provides access to workflow metadata for a specific scope.
 * Workflow metadata defines:
 * - Valid status options for an object type
 * - Allowed transitions between statuses
 * - Required fields for status changes (reasons, notes)
 * - Permissions and rules for status changes
 *
 * **Architecture:**
 * - Fetches workflow metadata for a specific scope (e.g., 'assignments.opportunity', 'staffing.need')
 * - Caches data using React Query (5 minutes stale time)
 * - Multiple components can share the same cached workflow data
 *
 * **Usage Pattern:**
 * ```typescript
 * // Basic usage - get workflow metadata
 * const { statuses, isLoading } = useWorkflowMetadata('assignments.opportunity');
 *
 * // Access specific status
 * const newStatus = statuses[StatusCodes.NEW];
 * console.log(newStatus.title); // "New"
 * console.log(newStatus.allowed_transitions); // ["READY_TO_SHARE", "PAUSED", "CLOSED"]
 *
 * // Get all statuses as array
 * const statusArray = Object.values(statuses || {});
 *
 *
 * **Performance:**
 * - First call for a scope triggers API fetch
 * - Subsequent calls use cached data for 5 minutes
 * - Each scope (e.g., 'assignments.opportunity', 'staffing.need') is cached separately
 *
 * @param scope - The workflow scope identifier (e.g., 'assignments.opportunity', 'staffing.need')
 * @returns Query result containing workflow statuses and loading state
 *
 * @example
 * ```typescript
 * const { statuses, isLoading, error } = useWorkflowMetadata('staffing.need');
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error />;
 *
 * return (
 *   <div>
 *     {Object.values(statuses || {}).map(status => (
 *       <div key={status.code}>{status.title}</div>
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useWorkflowMetadata(scope: string) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['workflowsMeta', scope],
    queryFn: () =>
      WorkflowsService.workflowsMetaRetrieve({
        scope: [scope],
      }),
    staleTime: Infinity,
    cacheTime: 24 * 60 * 60 * 1000, // 1 day
  });

  // Extract just the workflow data for this scope
  // API returns { "staffing.need": { ... } }, we want just the { ... } part
  const statuses: WorkflowMetadata =
    (data as WorkflowMetadataResponse)?.[scope] ?? ({} as WorkflowMetadata);

  return {
    statuses,
    isLoading,
    error,
  };
}
